import React, { useState, useEffect } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { DataTable, platformBadge } from "./shared";

export default function StockAlertView({ write }) {
  const [cfg, setCfg] = useState(null);
  const [rows, setRows] = useState([]);
  const [running, setRunning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showUnmapped, setShowUnmapped] = useState(false);

  const load = () => api.get("/desty/stock-alert-log").then((r) => setRows(r.data.rows || [])).catch(() => {});
  useEffect(() => {
    api.get("/desty/autokeep-config").then((r) => setCfg(r.data)).catch(() => {});
    load();
  }, []);

  const toggleEnabled = async (v) => {
    if (!cfg) return;
    const prev = cfg;
    const next = { ...cfg, stock_alert_enabled: v };
    setCfg(next); setSaving(true);
    try { await api.put("/desty/autokeep-config", next); toast.success(v ? "Notif stok diaktifkan (jalan tiap 15 menit)" : "Notif stok dimatikan"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setCfg(prev); }
    finally { setSaving(false); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const s = (await api.post("/desty/stock-alert-run")).data || {};
      if (s.ran === false) toast.error(s.reason || "Notif stok Desty nonaktif — aktifkan dulu");
      else if (s.error) toast.error("Gagal cek stok Desty: " + s.error);
      else toast.success(`Cek stok selesai: ${s.new_alerts || 0} SKU stok 0 baru terdeteksi`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setRunning(false); }
  };

  const cols = [
    { key: "sku", header: "SKU", className: "font-mono text-xs", sortAccessor: (r) => r.sku, render: (r) => r.sku },
    { key: "product_name", header: "Produk", className: "text-xs max-w-xs", render: (r) => r.product_name || "-" },
    { key: "platforms", header: "Marketplace", render: (r) => <span className="flex gap-1 flex-wrap">{(r.platforms || []).map((p, i) => <span key={i}>{platformBadge(p)}</span>)}</span> },
    { key: "erp", header: "Stok ERP", align: "right", sortAccessor: (r) => (r.erp_available == null ? -1 : r.erp_available), render: (r) => r.erp_available == null ? <span className="text-cocoa/40 text-xs">belum termapping</span> : r.erp_available },
    { key: "status", header: "Ketersediaan", render: (r) => r.can_refill ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Masih bisa diisi</Badge> : <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Kosong juga di ERP</Badge> },
  ];
  const filters = [
    { key: "platform", label: "Platform", allLabel: "Semua platform", options: [{ value: "shopee", label: "Shopee" }, { value: "tiktok", label: "Tiktok" }], match: (r, v) => (r.platforms || []).includes(v) },
    { key: "refill", label: "Ketersediaan", allLabel: "Semua", options: [{ value: "yes", label: "Masih bisa diisi" }, { value: "no", label: "Kosong juga" }], match: (r, v) => (v === "yes" ? r.can_refill : !r.can_refill) },
  ];

  const mappedRows = rows.filter((r) => r.erp_available != null);
  const unmappedCount = rows.length - mappedRows.length;
  const displayRows = showUnmapped ? rows : mappedRows;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3 rounded-lg border border-cocoa/10 bg-white p-4">
        <div>
          <div className="font-semibold text-cocoa flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Notif Stok Desty Habis</div>
          <p className="text-xs text-cocoa/60">Pantau SKU yang stoknya 0 di marketplace (Shopee/Tiktok) lengkap dengan cek stok ERP kita. Berjalan otomatis tiap 15 menit + notifikasi in-app & email.</p>
        </div>
        <div className="flex items-center gap-3">
          {write && <div className="flex items-center gap-2 text-sm"><span className="text-cocoa/60">Aktif</span><Switch checked={!!cfg?.stock_alert_enabled} disabled={saving || !cfg} onCheckedChange={toggleEnabled} data-testid="stock-master-toggle" /></div>}
          {write && <Button variant="outline" onClick={runNow} disabled={running} data-testid="stock-run-now"><RefreshCw className={`w-4 h-4 mr-1.5 ${running ? "animate-spin" : ""}`} /> {running ? "Mengecek…" : "Cek Stok Sekarang"}</Button>}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="stock-summary">
        <span className="rounded-full bg-cocoa/10 text-cocoa px-2.5 py-1">Termapping ERP: <b>{mappedRows.length}</b></span>
        <span className="rounded-full bg-emerald-100 text-emerald-700 px-2.5 py-1">Masih bisa diisi (ERP ada): <b>{rows.filter((r) => r.can_refill).length}</b></span>
        <span className="rounded-full bg-red-100 text-red-700 px-2.5 py-1">Kosong juga di ERP: <b>{mappedRows.filter((r) => !r.can_refill).length}</b></span>
        <span className="rounded-full bg-slate-100 text-slate-500 px-2.5 py-1">Belum termapping: <b>{unmappedCount}</b></span>
        <label className="ml-auto flex items-center gap-2 text-cocoa/70 cursor-pointer">
          <Switch checked={showUnmapped} onCheckedChange={setShowUnmapped} data-testid="stock-show-unmapped-toggle" />
          Tampilkan yang belum termapping
        </label>
      </div>
      <DataTable testid="stock-log-table" columns={cols} rows={displayRows} filters={filters}
        searchAccessors={[(r) => r.sku, (r) => r.product_name]} searchPlaceholder="Cari SKU / produk…"
        emptyText={showUnmapped ? "Belum ada SKU stok habis terdeteksi. Klik 'Cek Stok Sekarang' untuk menarik data." : "Belum ada SKU termapping yang stoknya habis. Aktifkan 'Tampilkan yang belum termapping' untuk melihat sisanya."} />
    </div>
  );
}
